import type { IReportExportPort } from '@reports/application/ports';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type { ExportConfig } from '@reports/domain/value-objects';
import type { ReportsHttpClientType } from '@reports/infrastructure/api/types';
import type { ReportApiMapperType } from '@reports/infrastructure/mappers/types';

/**
 * Creates a repository for Report Export operations
 *
 * Implements IReportExportPort by coordinating between the HTTP client and mappers.
 * Converts between application Dtos and API types.
 *
 * @param props - Configuration object
 * @param props.httpClient - HTTP client instance for making API requests
 * @param props.mapper - Mapper instance for converting between Dtos and API types
 * @returns ReportsExportRepository instance implementing IReportExportPort
 *
 * @example
 * const exportRepository = createReportsExportRepository({
 *   httpClient: reportsHttpClient,
 *   mapper: reportApiMapper,
 * });
 */
export const createReportsExportRepository = (props: {
  httpClient: ReportsHttpClientType;
  mapper: ReportApiMapperType;
}): IReportExportPort => {
  const { httpClient, mapper } = props;

  /**
   * Exports report data to a file blob
   *
   * @param request - Block data request with filters (Dto)
   * @param exportConfig - Export configuration from report definition (includes endpoint and slugs)
   * @returns Promise with file blob
   */
  const exportReport = async (request: BlockDataRequestDto, exportConfig: ExportConfig): Promise<Blob> => {
    const apiRequest = mapper.mapBlockDataRequestDtoToApiRequest(request);

    // Add slugs array to the API request
    const apiRequestWithSlugs = {
      ...apiRequest,
      queries: exportConfig.slugs as string[],
    };

    const blob = await httpClient.fetchExportReport(apiRequestWithSlugs, exportConfig.endpoint);

    return blob;
  };

  return {
    exportReport,
  };
};
